import React from 'react';
import { useAuthAccess } from './AuthAccessProvider';
import { AUTH_CONFIG } from './authConfig';

function LoadingState() {
  return (
    <section className="page">
      <div className="page-header">
        <div className="section-label">Loading</div>
      </div>
    </section>
  );
}

function SignInRequired() {
  return (
    <section className="page">
      <div className="page-header">
        <div className="section-label">Sign in required</div>
        <h1>Sign in to ExecEye</h1>
        <p className="assistant-info">Use your London Business School Microsoft account to continue.</p>
      </div>
    </section>
  );
}

function AccessDenied({ detail, noApplicationAccess = false, onLoginAgain }) {
  return (
    <section className="page">
      <div className="page-header">
        <div className="section-label">Access denied</div>
        <h1>{noApplicationAccess ? 'You do not have access to this application' : 'Permission required'}</h1>
        {noApplicationAccess ? (
          <>
            <p className="assistant-info">
              {detail || 'Your account does not include the required ExecEye permissions.'}
            </p>
            <button type="button" className="btn-primary" onClick={onLoginAgain}>
              Return to login
            </button>
          </>
        ) : (
          <p className="assistant-info">Your account does not have permission to open this area.</p>
        )}
      </div>
    </section>
  );
}

function RequireAuth({ children, permission }) {
  const {
    authorizationError,
    hasPermission,
    isAuthenticated,
    isAuthorizing,
    isLoading,
    logout
  } = useAuthAccess();

  const returnToLogin = () => {
    logout();
  };

  if (isLoading || isAuthorizing) {
    return <LoadingState />;
  }

  if (!isAuthenticated) {
    return <SignInRequired />;
  }

  const hasApplicationAccess = hasPermission(AUTH_CONFIG.permissions.user)
    || hasPermission(AUTH_CONFIG.permissions.admin);

  if (authorizationError) {
    return (
      <AccessDenied
        detail={authorizationError.message}
        noApplicationAccess
        onLoginAgain={returnToLogin}
      />
    );
  }

  if (permission && !hasPermission(permission)) {
    return (
      <AccessDenied
        noApplicationAccess={!hasApplicationAccess}
        onLoginAgain={returnToLogin}
      />
    );
  }

  return children;
}

export default RequireAuth;
